import { Modal } from '@simplywallst/ui-core';
import type { Member } from '@/pages/CompanyReport/components/Management';
import {
  SectionColumn,
  Header,
  SecondaryText,
  SecondaryTextSmall,
} from './styled';
import { toSignificant, toTruncated } from '@/utilities/formatting';
import { Row } from '@simplywallst/ui-core';
import type { TeamType } from '../../../Management';
import { useManagementTranslations } from '../../../hooks/useManagementTranslations';

interface TeamMemberModalProps {
  member: Member;
  teamType: TeamType;
  marketCapListing: number;
  currencySymbol: string;
  onClose: () => void;
}
const TeamMemberModal = ({
  member,
  teamType,
  marketCapListing,
  currencySymbol,
  onClose,
}: TeamMemberModalProps) => {
  const memberFirstName =
    member.salutation !== '' ? member.salutation : member.firstName;
  const memberName = `${memberFirstName} ${member.lastName}`;
  const t = useManagementTranslations();
  const renderTenure = () => {
    if (teamType === 'board') {
      if (member.formattedBoardTenure === 'no data') {
        return (
          <SecondaryTextSmall>{member.formattedBoardTenure}</SecondaryTextSmall>
        );
      }
      return member.formattedBoardTenure;
    }
    if (teamType === 'management') {
      if (member.formattedManagementTenure === 'no data') {
        return (
          <SecondaryTextSmall>
            {member.formattedManagementTenure}
          </SecondaryTextSmall>
        );
      }
      return member.formattedManagementTenure;
    }
  };
  return (
    <Modal
      title={memberName}
      onClose={onClose}
      fullscreen={false}
      secondaryLabel="Close"
      onSecondaryClick={onClose}
    >
      <>
        <Row noWrap={true}>
          <SectionColumn alignItem="flex-start" fillVoid>
            <Header>{t('team.heading2')}</Header>
            <summary>
              {member.shortTitle === '' ? (
                <SecondaryTextSmall>{t('metrics.noData')}</SecondaryTextSmall>
              ) : (
                member.shortTitle
              )}
            </summary>
          </SectionColumn>
          <SectionColumn alignItem="flex-end" width="auto">
            <Header>{t('leader.tenure')}</Header>
            <summary>{renderTenure()}</summary>
          </SectionColumn>
          <SectionColumn alignItem="flex-end" width="auto">
            <Header>{t('age')}</Header>
            <summary>
              {member.age > 0 ? (
                member.age
              ) : (
                <SecondaryTextSmall>{t('metrics.noData')}</SecondaryTextSmall>
              )}
            </summary>
          </SectionColumn>
        </Row>
        <SectionColumn>
          <Header>{t('compensationPerYear')}</Header>
          <summary>
            {member.compensation === '' ? (
              <SecondaryTextSmall>{t('metrics.noData')}</SecondaryTextSmall>
            ) : (
              `${member.compensation}`
            )}
          </summary>
        </SectionColumn>
        <SectionColumn>
          <Header>{t('companyOwnership')}</Header>
          <summary>
            {member.percentSharesOutstanding === '' ? (
              <SecondaryTextSmall>{t('metrics.noData')}</SecondaryTextSmall>
            ) : (
              <>
                <span>{`${toSignificant(
                  parseFloat(member.percentSharesOutstanding),
                  2
                )} %`}</span>
                <SecondaryText>
                  {`${currencySymbol} ${toTruncated(
                    (parseFloat(member.percentSharesOutstanding) / 100) *
                      marketCapListing,
                    1
                  )}`}
                </SecondaryText>{' '}
                <SecondaryTextSmall>
                  {t('numberShares', {
                    numberOfShares: toTruncated(
                      parseFloat(member.sharesHeld),
                      1
                    ),
                  })}
                </SecondaryTextSmall>
              </>
            )}
          </summary>
        </SectionColumn>
        <SectionColumn>
          <Header>{t('profile')}</Header>
          <p>
            {member.biography === '' ? (
              <SecondaryTextSmall>{t('metrics.noData')}</SecondaryTextSmall>
            ) : (
              member.biography
            )}
          </p>
        </SectionColumn>
      </>
    </Modal>
  );
};

export default TeamMemberModal;
